import React, { useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import styles from './TrailMapPreview.styles';
import TrailMap from './TrailMap';
import { Coordinate } from '@/types/trail';

interface TrailMapPreviewProps {
  coordinates?: Coordinate[];
  path?: number[][] | null;
  style?: any;
  startLatitude?: number | null;
  startLongitude?: number | null;
  showOnlyPath?: boolean;
  onPress?: () => void;
}

export default function TrailMapPreview({ coordinates, path, style, startLatitude = null, startLongitude = null, showOnlyPath = false, onPress }: TrailMapPreviewProps) {
  const coords = useMemo(() => {
    if (coordinates && coordinates.length > 0) {
      // Filter out any coordinates with NaN/Infinity values
      return coordinates.filter(c => Number.isFinite(c.latitude) && Number.isFinite(c.longitude));
    }
    if (Array.isArray(path)) {
      // Filter out any path points with NaN/Infinity values
      return path
        .filter(p => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
        .map(p => ({ latitude: p[1], longitude: p[0] }));
    }
    return [];
  }, [coordinates, path]);

  const region = useMemo(() => {
    if (coords.length === 0 && (startLatitude == null || startLongitude == null)) {
      return {
        latitude: 40.7128,
        longitude: -74.0060,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      };
    }

    // If explicit start coordinates are provided prefer centering on them.
    if (Number.isFinite(startLatitude) && Number.isFinite(startLongitude)) {
      return {
        latitude: startLatitude!,
        longitude: startLongitude!,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      };
    }

    if (coords.length === 0) {
      return {
        latitude: 40.7128,
        longitude: -74.0060,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      };
    }

    const lats = coords.map(ci => ci.latitude);
    const lons = coords.map(ci => ci.longitude);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    const latDelta = Math.max((maxLat - minLat) * 1.3, 0.005);
    const lonDelta = Math.max((maxLon - minLon) * 1.3, 0.005);

    return {
      latitude: centerLat,
      longitude: centerLon,
      latitudeDelta: latDelta,
      longitudeDelta: lonDelta,
    };
  }, [coords, startLatitude, startLongitude]);

  return (
    <View style={[styles.container, style]}>
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <TrailMap
          style={styles.map}
          initialRegion={region}
          scrollEnabled={false}
          zoomEnabled={false}
          coordinates={coords}
          showOnlyPath={showOnlyPath}
        />
      </View>
      {onPress && (
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          onPress={onPress}
          activeOpacity={1}
        />
      )}
    </View>
  );
}

// styles imported from TrailMapPreview.styles.ts